/**
 * Normalize phone number to E.164 format (required by Cognito)
 * E.164 format: +[country code][number] (e.g., +27846001275)
 */
export function normalizePhoneNumber(phoneNumber: string): string {
  // Remove all non-digit characters except +
  let cleaned = phoneNumber.trim();

  // If it already starts with +, return as is (assuming it's already in E.164)
  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  // Remove any non-digit characters
  const cleanNumber = cleaned.replace(/\D/g, "");

  // Check if it's a South African number (starts with 0 and is 10 digits)
  if (cleanNumber.startsWith("0") && cleanNumber.length === 10) {
    // Convert SA format (0xx xxx xxxx) to international (+27 xx xxx xxxx)
    return `+27${cleanNumber.substring(1)}`;
  } else if (cleanNumber.length === 9 && !cleanNumber.startsWith("0")) {
    // Already without leading 0, just add +27 (South Africa)
    return `+27${cleanNumber}`;
  } else if (cleanNumber.length >= 10) {
    // For other formats, assume US/Canada (+1) if no country code provided
    // This is a fallback - ideally users should provide full E.164 format
    return `+1${cleanNumber}`;
  } else {
    // If we can't determine, just add + and hope the user provided correct format
    // This will likely fail Cognito validation, but at least we tried
    return `+${cleanNumber}`;
  }
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

function buildPhoneSearchVariants(query: string): string[] {
  const variants = new Set<string>();
  const trimmed = query.trim();
  variants.add(trimmed);

  const digits = digitsOnly(trimmed);
  if (digits) {
    variants.add(digits);
  }

  variants.add(normalizePhoneNumber(trimmed));

  if (digits.startsWith("0") && digits.length >= 2) {
    variants.add(`+27${digits.slice(1)}`);
    variants.add(digits.slice(1));
  }

  if (digits.startsWith("27") && digits.length > 2) {
    variants.add(`+${digits}`);
    const national = digits.slice(2);
    variants.add(`0${national}`);
    variants.add(national);
  }

  if (
    digits.length === 9 &&
    !digits.startsWith("0") &&
    !digits.startsWith("27")
  ) {
    variants.add(`+27${digits}`);
    variants.add(`0${digits}`);
  }

  return Array.from(variants).filter(Boolean);
}

/** Match stored E.164 phones when the user searches 0xx, +27xx, or partial digits. */
export function phoneMatchesSearch(
  storedPhone: string | undefined | null,
  searchQuery: string
): boolean {
  if (!storedPhone?.trim() || !searchQuery.trim()) {
    return false;
  }

  const q = searchQuery.trim();
  if (!/^[\d\s+\-().]+$/.test(q)) {
    return false;
  }

  const storedNorm = normalizePhoneNumber(storedPhone);
  const storedDigits = digitsOnly(storedNorm);

  for (const variant of buildPhoneSearchVariants(q)) {
    if (storedNorm.includes(variant) || storedPhone.includes(variant)) {
      return true;
    }
    const variantDigits = digitsOnly(variant);
    if (variantDigits.length >= 3 && storedDigits.includes(variantDigits)) {
      return true;
    }
  }

  return false;
}

